/**
 * Konversi Masehi ↔ Hijriah berbasis algoritma aritmatika tabular Islamic
 * (Kuwaiti / arithmetic civil calendar). Hasil bisa berbeda 1 hari dengan
 * pengamatan rukyat / SKB; gunakan data libur resmi untuk hari raya.
 */

import { julianDay } from "./dates";

export const HIJRI_MONTHS = [
  "Muharram",
  "Safar",
  "Rabiul Awal",
  "Rabiul Akhir",
  "Jumadil Awal",
  "Jumadil Akhir",
  "Rajab",
  "Syaban",
  "Ramadhan",
  "Syawal",
  "Zulkaidah",
  "Zulhijah",
];

export const HIJRI_MONTHS_EN = [
  "Muharram",
  "Safar",
  "Rabi al-Awwal",
  "Rabi al-Thani",
  "Jumada al-Awwal",
  "Jumada al-Thani",
  "Rajab",
  "Shaban",
  "Ramadan",
  "Shawwal",
  "Dhul-Qadah",
  "Dhul-Hijjah",
];

export const HIJRI_MONTH_SLUGS_ID = [
  "muharram", "safar", "rabiul-awal", "rabiul-akhir",
  "jumadil-awal", "jumadil-akhir", "rajab", "syaban",
  "ramadhan", "syawal", "zulkaidah", "zulhijah",
];

export const HIJRI_MONTH_SLUGS_EN = [
  "muharram", "safar", "rabi-al-awwal", "rabi-al-thani",
  "jumada-al-awwal", "jumada-al-thani", "rajab", "shaban",
  "ramadan", "shawwal", "dhul-qadah", "dhul-hijjah",
];

/** Build pretty slug like `ramadhan-1447`. Locale-aware. */
export function hijriMonthSlug(hYear: number, hMonth: number, locale: "id" | "en" = "id"): string {
  const slugs = locale === "en" ? HIJRI_MONTH_SLUGS_EN : HIJRI_MONTH_SLUGS_ID;
  return `${slugs[hMonth - 1]}-${hYear}`;
}

/** Parse `ramadhan-1447` (or EN equivalent) -> {hYear, hMonth}. Returns null if invalid. */
export function parseHijriMonthSlug(s: string): { hYear: number; hMonth: number } | null {
  const m = /^([a-z-]+)-(\d{3,4})$/.exec(s);
  if (!m) return null;
  const slug = m[1];
  const yr = parseInt(m[2], 10);
  let idx = HIJRI_MONTH_SLUGS_ID.indexOf(slug);
  if (idx < 0) idx = HIJRI_MONTH_SLUGS_EN.indexOf(slug);
  if (idx < 0) return null;
  if (yr < 1300 || yr > 1600) return null;
  return { hYear: yr, hMonth: idx + 1 };
}

export interface HijriDate {
  year: number;
  month: number; // 1-12
  day: number; // 1-30
}

/** Gregorian -> Hijri (arithmetic, civil epoch JD=1948440 = 16 Jul 622 CE) */
export function gregorianToHijri(year: number, month: number, day: number): HijriDate {
  const jd = julianDay(year, month, day);
  const l0 = jd - 1948440 + 10632;
  const n = Math.floor((l0 - 1) / 10631);
  const l1 = l0 - 10631 * n + 354;
  const j =
    Math.floor((10985 - l1) / 5316) * Math.floor((50 * l1) / 17719) +
    Math.floor(l1 / 5670) * Math.floor((43 * l1) / 15238);
  const l2 =
    l1 -
    Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) -
    Math.floor(j / 16) * Math.floor((15238 * j) / 43) +
    29;
  const m = Math.floor((24 * l2) / 709);
  const d = l2 - Math.floor((709 * m) / 24);
  const y = 30 * n + j - 30;
  return { year: y, month: m, day: d };
}

export function formatHijri(h: HijriDate): string {
  return `${h.day} ${HIJRI_MONTHS[h.month - 1]} ${h.year} H`;
}

export function formatHijriLocale(h: HijriDate, locale: "id" | "en"): string {
  const names = locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS;
  return `${h.day} ${names[h.month - 1]} ${h.year} ${locale === "en" ? "AH" : "H"}`;
}

/** Hijri -> Gregorian (inverse of arithmetic Kuwaiti algorithm). Returns {y,m,d}. */
export function hijriToGregorian(hYear: number, hMonth: number, hDay: number): { y: number; m: number; d: number } {
  // JD of given hijri date (arithmetic civil)
  const jd =
    Math.floor((11 * hYear + 3) / 30) +
    354 * hYear +
    30 * hMonth -
    Math.floor((hMonth - 1) / 2) +
    hDay +
    1948055; // calibrated to inverse of arithmetic gregorianToHijri above
  return jdToGregorian(jd);
}

/** JD -> Gregorian. */
export function jdToGregorian(jd: number): { y: number; m: number; d: number } {
  const a = jd + 32044;
  const b = Math.floor((4 * a + 3) / 146097);
  const c = a - Math.floor((146097 * b) / 4);
  const d = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor((1461 * d) / 4);
  const m = Math.floor((5 * e + 2) / 153);
  const day = e - Math.floor((153 * m + 2) / 5) + 1;
  const month = m + 3 - 12 * Math.floor(m / 10);
  const year = 100 * b + d - 4800 + Math.floor(m / 10);
  return { y: year, m: month, d: day };
}

/** Number of days in given Hijri month (29 or 30). */
export function daysInHijriMonth(hYear: number, hMonth: number): number {
  const start = hijriToGregorian(hYear, hMonth, 1);
  const next = hMonth === 12
    ? hijriToGregorian(hYear + 1, 1, 1)
    : hijriToGregorian(hYear, hMonth + 1, 1);
  const jdA = julianDay(start.y, start.m, start.d);
  const jdB = julianDay(next.y, next.m, next.d);
  return jdB - jdA;
}

/** Today's Hijri year. */
export function currentHijriYear(): number {
  const now = new Date();
  return gregorianToHijri(now.getFullYear(), now.getMonth() + 1, now.getDate()).year;
}